import React from "react";
import { ReactComponent as XClose } from "../../assets/icons/x-close.svg";

interface ModalHeaderProps {
  /** Title text to display in the header */
  title: string;
  
  /** Icon component to show in the header Pass a ReactComponent import like <HomeSmile /> */
  icon?: React.ReactNode;
  
  /** Function to call when close button is clicked */
  onClose: () => void;
  
  /** Optional class to apply to the header container */
  className?: string;

  /** Horizontal gutter. Defaults to the legacy 20px used by hand-rolled modals; ModalBase passes the shared `eai-modal-gutter` so the icon lines up with the body at every width. */
  gutterClassName?: string;

  /** Small inline element after the title, such as a status pill */
  titleAdornment?: React.ReactNode;

  /** Optional content to render in the middle section (replacing title) */
  children?: React.ReactNode;
}

/** Reusable modal header component with standardized styling @example <ModalHeader title="Edit Asset" icon={<HomeSmile className="h-6 w-6" />} onClose={() => dispatch(setModalOpen(false))} /> */
const ModalHeader: React.FC<ModalHeaderProps> = ({ 
  title, 
  icon, 
  onClose,
  className = "",
  gutterClassName = "px-5",
  titleAdornment,
  children
}) => {
  return (
    <div className={`flex flex-row items-center border-b border-eai-border py-5 ${gutterClassName} ${className}`}>
      {icon && (
        <div className="eai-card flex flex-0 w-10 h-10 mr-3 items-center justify-center">
          {icon}
        </div>
      )}
      {children ? (
        children
      ) : titleAdornment ? (
        <div className="flex grow min-w-0 items-center gap-2.5">
          <div className="eai-h3-title min-w-0 truncate" title={title}>{title}</div>
          <div className="shrink-0">{titleAdornment}</div>
        </div>
      ) : (
        <div className="eai-h3-title grow min-w-0 truncate" title={title}>
          {title}
        </div>
      )}
      {/* type="button" matters — several modals render this header inside a <form> */}
      <button
        type="button"
        onClick={() => onClose()}
        aria-label="Close"
        className="shrink-0 flex w-10 h-10 items-center justify-center rounded-full text-eai-ink-subtle transition-colors hover:bg-eai-surface-hover hover:text-eai-ink active:bg-eai-surface-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eai-focus-neutral focus-visible:ring-offset-1"
      >
        <XClose className="h-5 w-5 [&_path]:stroke-current" />
      </button>
    </div>
  );
};

export default ModalHeader;